import React, { useState, useCallback } from 'react';
import { Module, Simulation } from '../../lib/supabase';
import { QuizStep } from './QuizStep';
import { MiniSimStep } from './MiniSimStep';
import './module-player.scss';

interface ModulePlayerProps {
    module: Module;
    onBack: () => void;
    onStartMainSimulation: (sim: Simulation) => void;
}

export const ModulePlayer: React.FC<ModulePlayerProps> = ({ module, onBack, onStartMainSimulation }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [completed, setCompleted] = useState(false);

    const items = module.items;
    const currentItem = items[currentStep];
    const progress = items.length > 0 ? ((currentStep) / items.length) * 100 : 0;

    const goNext = useCallback(() => {
        if (currentStep < items.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            setCompleted(true);
        }
    }, [currentStep, items.length]);

    const handleQuizComplete = useCallback((_correct: boolean) => {
        // For now, just proceed regardless of correctness
        goNext();
    }, [goNext]);

    const handleMiniSimComplete = useCallback(() => {
        goNext();
    }, [goNext]);

    const handleMainSimStart = useCallback(() => {
        if (currentItem?.type === 'main_sim') {
            // Convert MainSimItem to a Simulation-like object for the existing StreamingConsole
            const simConfig: Simulation = {
                id: `module-${module.id}-step-${currentStep}`,
                institution_id: module.institution_id,
                title: module.title,
                is_active: true,
                config: {
                    systemInstruction: {
                        parts: [{ text: currentItem.systemInstruction }]
                    },
                    voice: currentItem.voice,
                    avatarUrl: currentItem.avatarUrl,
                    missions: currentItem.missions,
                    userInstruction: currentItem.userInstruction
                }
            };
            onStartMainSimulation(simConfig);
        }
    }, [currentItem, currentStep, module, onStartMainSimulation]);

    if (completed) {
        return (
            <div className="module-player">
                <div className="module-complete">
                    <div className="complete-icon-wrapper">
                        <span className="material-symbols-outlined">emoji_events</span>
                    </div>
                    <h2>Modül Tamamlandı!</h2>
                    <p>{module.title} modülünü başarıyla tamamladınız.</p>
                    <button className="btn-back-to-modules" onClick={onBack}>
                        <span className="material-symbols-outlined">arrow_back</span>
                        Modüllere Dön
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="module-player">
            {/* Header */}
            <header className="module-player-header">
                <button className="btn-close-module" onClick={onBack}>
                    <span className="material-symbols-outlined">close</span>
                </button>
                <div className="progress-area">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                    <span className="step-counter">{currentStep + 1} / {items.length}</span>
                </div>
                <div className="module-title">{module.title}</div>
            </header>

            {/* Content */}
            <div className="module-player-content">
                {currentItem?.type === 'quiz' && (
                    <QuizStep
                        key={currentStep}
                        item={currentItem}
                        onComplete={handleQuizComplete}
                    />
                )}

                {currentItem?.type === 'mini_sim' && (
                    <MiniSimStep
                        key={currentStep}
                        item={currentItem}
                        onComplete={handleMiniSimComplete}
                    />
                )}

                {currentItem?.type === 'main_sim' && (
                    <div className="main-sim-prompt">
                        <div className="sim-icon-wrapper">
                            <span className="material-symbols-outlined">smart_toy</span>
                        </div>
                        <h2>Ana Simülasyon</h2>
                        <p>Tüm hazırlık adımları tamamlandı. Şimdi ana simülasyona başlayabilirsiniz.</p>
                        {currentItem.userInstruction && (
                            <div className="user-instruction-preview">
                                <strong>Talimatlar:</strong>
                                <p>{currentItem.userInstruction}</p>
                            </div>
                        )}
                        <button className="btn-start-main-sim" onClick={handleMainSimStart}>
                            <span className="material-symbols-outlined">play_arrow</span>
                            Simülasyonu Başlat
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
